import { BuilderElement, ElementType, GlobalConfig } from "@/types/builder";
import {
  createMentionedInSection,
  createListingSection,
  createFooterMenuSection,
  createFooterEndSection,
  createPartnerMenuSection,
} from "./template2";

const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

export const template2UpsellGlobalConfig: GlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Inter",
      fontSize: "40px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Inter",
      fontSize: "28px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Inter",
      fontSize: "18px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: "700",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#F3F6FF",
    forground: "#FFFFFF",
    primary: "#A44CF5",
    secondary: "#317CF7",
    tertiary: "#333333",
    quaternary: "#1a1a1a",
    link: "#A44CF5",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};

const template2UpsellFeatures = (globalConfig: GlobalConfig) => {
  return {
    id: generateId(),
    type: "container" as ElementType,
    styles: {
      width: "100%",
      maxWidth: "1338px",
      margin: "0 auto",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "30px",
      marginTop: "0",
    },
    responsiveStyles: {
      tablet: { padding: "20px 16px" },
      mobile: { padding: "20px 16px" },
    },
    children: [
      {
        id: generateId(),
        type: "container" as ElementType,
        styles: {
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "30px",
        },
        responsiveStyles: {
          tablet: { gap: "16px" },
        },
        children: [
          // Feature Box
          {
            id: generateId(),
            type: "container" as ElementType,
            styles: {
              width: "100%",
            },
            children: [
              // Feature Header
              {
                id: generateId(),
                type: "container" as ElementType,
                styles: {
                  backgroundImage: "url('/images/movyo/upsell/featureBox.svg')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  padding: "32px 0",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                },
                responsiveStyles: {
                  tablet: { padding: "16px 0" },
                },
                children: [
                  {
                    id: generateId(),
                    type: "heading" as ElementType,
                    dynamicContentId: "__dynamic__bottle_count__",
                    content:
                      "Get 3 More Bottles of Movyo for Just $87 Total (Only $29 Each)",
                    styles: {
                      maxWidth: "926px",
                      color: "background",
                      textAlign: "center",
                      fontSize: "40px",
                      fontWeight: "700",
                      lineHeight: "48px",
                      margin: "0",
                    },
                    typography: "Heading-2",
                    responsiveStyles: {
                      tablet: { fontSize: "35px", lineHeight: "40px" },
                      mobile: { fontSize: "26px", lineHeight: "33px" },
                    },
                  },
                ],
              },
              // Feature Body
              {
                id: generateId(),
                type: "container" as ElementType,
                styles: {
                  padding: "104px 40px 98px",
                  backgroundColor: "#F9F9F9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                },
                responsiveStyles: {
                  tablet: {
                    flexDirection: "column",
                    padding: "20px",
                    alignItems: "center",
                  },
                  mobile: {
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "10px",
                  },
                },
                children: [
                  // Image Container
                  {
                    id: generateId(),
                    type: "container" as ElementType,
                    styles: {
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                    },
                    children: [
                      {
                        id: generateId(),
                        type: "container" as ElementType,
                        styles: {
                          width: "469.365px",
                          height: "442px",
                          flexShrink: 0,
                          position: "relative",
                        },
                        responsiveStyles: {
                          mobile: { width: "100%", height: "100%" },
                        },
                        children: [
                          {
                            id: generateId(),
                            type: "image" as ElementType,
                            dynamicContentId: "__dynamic__bottle_image__",
                            src: "/images/movyo/dynamic-images/bottles-3.webp",
                            alt: "Bottles",
                            styles: {
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            },
                          },
                        ],
                      },
                    ],
                  },
                  // Content Body
                  {
                    id: generateId(),
                    type: "container" as ElementType,
                    styles: {
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "flex-start",
                      gap: "30px",
                      flex: 1,
                    },
                    responsiveStyles: {
                      tablet: { gap: "16px" },
                    },
                    children: [
                      {
                        id: generateId(),
                        type: "heading" as ElementType,
                        content: "Ensures a longer, uninterrupted supply",
                        styles: {
                          color: "link",
                          fontFamily: "Inter",
                          fontSize: "30px",
                          fontWeight: "600",
                          lineHeight: "100%",
                          margin: "0",
                        },
                        responsiveStyles: {
                          tablet: {
                            fontSize: "26px",
                            lineHeight: "33px",
                            textAlign: "center",
                          },
                          mobile: {
                            fontSize: "26px",
                            lineHeight: "26px",
                            textAlign: "center",
                          },
                        },
                      },
                      // Feature List
                      {
                        id: generateId(),
                        type: "list" as ElementType,
                        styles: {
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                          gap: "20px",
                        },
                        responsiveStyles: {
                          tablet: { gap: "16px" },
                        },
                        children: [
                          {
                            id: generateId(),
                            type: "listItem" as ElementType,
                            content: `<img src="/images/movyo/✓.svg" alt="check" style="width: 30px; height: 30px;" /> That's less than half the price of buying later`,
                            styles: {
                              color: "link",
                              fontSize: "24px",
                              fontWeight: "600",
                              lineHeight: "26px",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                            },
                            responsiveStyles: {
                              tablet: { fontSize: "22px", lineHeight: "30px" },
                              mobile: { fontSize: "18px", lineHeight: "24px" },
                            },
                          },
                          {
                            id: generateId(),
                            type: "listItem" as ElementType,
                            content: `<img src="/images/movyo/✓.svg" alt="check" style="width: 30px; height: 30px;" /> Perfect for deeper results, sharing with loved ones, or avoiding backorders`,
                            styles: {
                              color: "link",
                              fontSize: "24px",
                              fontWeight: "600",
                              lineHeight: "26px",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                            },
                            responsiveStyles: {
                              tablet: { fontSize: "22px", lineHeight: "30px" },
                              mobile: { fontSize: "18px", lineHeight: "24px" },
                            },
                          },
                        ],
                      },
                      // CTA Button
                      {
                        id: generateId(),
                        type: "button" as ElementType,
                        dynamicContentId: "__dynamic__button_offer__",
                        content: "YES, ADD 3 MORE BOTTLES FOR JUST $87",
                        styles: {
                          backgroundColor: "#F4B83D", // $primary-color
                          color: "forground", // $black
                          fontWeight: "700",
                          fontSize: "20px",
                          lineHeight: "24px",
                          padding: "10.5px 27.5px",
                          borderRadius: "24px", // $radius24
                          border: "none",
                          cursor: "pointer",
                          textTransform: "capitalize",
                          transition: "all 0.3s ease-in-out",
                        },
                        typography: "Button",
                        responsiveStyles: {
                          tablet: {
                            alignSelf: "center",
                            fontSize: "16px",
                            padding: "10px 27px",
                            height: "50px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          },
                          mobile: {
                            fontSize: "14px",
                            padding: "10px 27px",
                          },
                        },
                      },
                      // Payment Methods
                      {
                        id: generateId(),
                        type: "image" as ElementType,
                        src: "/images/movyo/pay-option1.webp",
                        alt: "Payment Methods",
                        styles: {
                          maxWidth: "324px",
                          width: "100%",
                          objectFit: "contain",
                        },
                        responsiveStyles: {
                          tablet: { alignSelf: "center" },
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // Feature Box Text (Downsell CTA)
          {
            id: generateId(),
            type: "paragraph" as ElementType,
            content:
              "<a href='#'>No thanks, I'll pass and risk losing my results later.</a>",
            styles: {
              color: "link",
              textAlign: "center",
              fontFamily: "Inter",
              fontSize: "30px",
              fontStyle: "italic",
              fontWeight: "300",
              lineHeight: "100%",
              textDecoration: "underline",
              margin: "0",
            },
            responsiveStyles: {
              tablet: { fontSize: "16px" },
            },
          },
        ],
      },
    ],
  };
};

export const template2Upsell = (globalConfig: GlobalConfig) => {
  return {
    specialOfferHeader: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        backgroundColor: "#FF0000",
        padding: "24px 0",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        margin: "0",
      },
      responsiveStyles: {
        tablet: { padding: "20px 0" },
        mobile: { padding: "16px 0" },
      },
      children: [
        {
          id: generateId(),
          type: "text" as ElementType,
          content: "SPECIAL OFFER ENDS IN: 05:00 - BUY NOW",
          styles: {
            color: "background",
            fontWeight: "900",
            fontSize: "30px",
            lineHeight: "100%",
            textAlign: "center",
            fontFamily: "Inter",
          },
          responsiveStyles: {
            tablet: {
              fontSize: "24px",
            },
            mobile: {
              fontSize: "16px",
            },
          },
        },
      ],
    },
    banner: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        padding: "0",
        margin: "0",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
      children: [
        // Offer Header Section
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            backgroundImage: "url('/images/movyo/bannerBG.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            padding: "80px 0 55px",
            textAlign: "center",
            fontFamily: "Inter",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          },
          responsiveStyles: {
            tablet: { padding: "60px 20px" },
            mobile: { padding: "40px 20px" },
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "44px",
                width: "100%",
              },
              responsiveStyles: {
                tablet: { gap: "30px" },
                mobile: { gap: "20px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "logo" as ElementType,
                  styles: {
                    maxWidth: "385px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  responsiveStyles: {
                    tablet: { maxWidth: "200px" },
                    mobile: { maxWidth: "150px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/images/movyo/logo-white.svg",
                      alt: "Logo",
                      styles: {
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "10px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "CONGRATULATIONS!",
                      styles: {
                        color: "background",
                        fontSize: "58.3px",
                        fontWeight: "700",
                        lineHeight: "58.3px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "30px", lineHeight: "30px" },
                        mobile: { fontSize: "20px", lineHeight: "20px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "YOU HAVE UNLOCKED A BONUS OFFER",
                      styles: {
                        color: "background",
                        fontSize: "58.3px",
                        fontWeight: "700",
                        lineHeight: "58.3px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "30px", lineHeight: "30px" },
                        mobile: { fontSize: "20px", lineHeight: "20px" },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "DO NOT CLOSE THIS PAGE! IT MAY CAUSE AN ERROR IN YOUR CONFIRMATION!",
                  styles: {
                    color: "background",
                    fontSize: "27.3px",
                    fontWeight: "400",
                    lineHeight: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "20px" },
                    mobile: { fontSize: "14px" },
                  },
                },
              ],
            },
          ],
        },
        // Offer Sub Header Section
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            backgroundColor: "#7B3DF5", // $gray-purple
            padding: "28.5px 0",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          },
          responsiveStyles: {
            tablet: { padding: "20px 16px" },
            mobile: { padding: "20px 16px" },
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "50px",
                width: "100%",
              },
              responsiveStyles: {
                tablet: { gap: "30px" },
                mobile: { gap: "20px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "WAIT! Before we ship your order...",
                  styles: {
                    color: "background",
                    fontSize: "48px",
                    fontWeight: "700",
                    lineHeight: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "38px" },
                    mobile: { fontSize: "20px" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    offer: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        backgroundColor: "#F3F6FF", // $light-bg
        marginBottom: "100px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            flexDirection: "row",
          },
          responsiveStyles: {
            mobile: { flexDirection: "column" },
          },
          children: [
            // Left Side
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                padding: "0 72px",
                height: "100%",
                width: "50%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              },
              responsiveStyles: {
                mobile: { padding: "50px 15px", width: "100%" },
                tablet: { padding: "30px", width: "50%", maxWidth: "50%" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content:
                    "Unlock the Secret to Effortless, Lasting Weight Loss",
                  styles: {
                    color: "forground",
                    fontSize: "40px",
                    fontWeight: "700",
                    letterSpacing: "-0.6px",
                    paddingBottom: "16px",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    mobile: { fontSize: "24px" },
                    tablet: { fontSize: "32px" },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content: `If you're tired of spending thousands on diets, injections, and programs that never deliver lasting results, you're not alone. This is your chance to lock  in long-term transformation at the deepest discount we'll ever offer`,
                  styles: {
                    color: "forground",
                    fontSize: "24px",
                    fontWeight: "500",
                    lineHeight: "32px",
                  },
                  responsiveStyles: {
                    mobile: { fontSize: "18px", lineHeight: "24px" },
                    tablet: { fontSize: "18px", lineHeight: "24px" },
                  },
                },
              ],
            },
            // Right Side
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                backgroundImage:
                  "url('/images/movyo/upsell/secretOffer-bg.svg')",
                backgroundColor: "background",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "50%",
                padding: "97px 0 108px",
              },
              responsiveStyles: {
                mobile: { width: "100%", padding: "10px 0" },
                tablet: { padding: "30px 0", width: "50%", maxWidth: "50%" },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/images/movyo/dynamic-images/bottles-3.webp",
                  alt: "Bottles",
                  styles: {
                    width: "408px",
                  },
                  responsiveStyles: {
                    tablet: { width: "300px" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    features: template2UpsellFeatures(globalConfig),
    features2: template2UpsellFeatures(globalConfig),
    guarantee: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        backgroundImage: "url('/images/movyo/upsell/featureBox.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        padding: "56px 0",
        margin: "100px 0 66px",
        width: "100%",
      },
      responsiveStyles: {
        tablet: { padding: "40px 16px" },
        mobile: { padding: "20px 16px" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "78px",
            maxWidth: "1050px",
            margin: "0 auto",
            width: "100%",
          },
          responsiveStyles: {
            mobile: {
              flexDirection: "column",
              alignItems: "center",
              gap: "30px",
            },
            tablet: { gap: "25px" },
          },
          children: [
            // Image Container
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                maxWidth: "251px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
              responsiveStyles: {
                mobile: { maxWidth: "200px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/images/movyo/upsell/moneyback.svg",
                  alt: "Money Back Guarantee",
                  styles: {
                    width: "100%",
                    objectFit: "contain",
                  },
                },
              ],
            },
            // Content Container
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                flex: 1,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "12px",
                flexDirection: "column",
              },
              responsiveStyles: {
                mobile: { alignItems: "center" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "60 Days money back guarantee",
                  styles: {
                    color: "background",
                    fontSize: "36px",
                    fontWeight: "700",
                    lineHeight: "150%",
                    margin: "0",
                  },
                  typography: "Heading-3",
                  responsiveStyles: {
                    tablet: { fontSize: "32px", lineHeight: "100%" },
                    mobile: { fontSize: "30px", textAlign: "center" },
                  },
                },
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "Our aim is for 100% customer satisfaction",
                  styles: {
                    color: "background",
                    fontSize: "26px",
                    fontWeight: "700",
                    lineHeight: "150%",
                    fontStyle: "normal",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "20px" },
                    mobile: { fontSize: "26px", textAlign: "center" },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "Movyo is a specialized formula designed to support the vagus nerve, essential for managing the movement of food from the stomach to the intestines. Our formula supports a healthy inflammatory response, along with the normal functioning of the vagus nerve.",
                  styles: {
                    fontSize: "16px",
                    fontWeight: "400",
                    lineHeight: "24px",
                    color: "background",
                    margin: "0",
                  },
                  typography: "Default",
                  responsiveStyles: {
                    tablet: { fontSize: "16px", textAlign: "left" },
                    mobile: { fontSize: "18px", textAlign: "center" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    risk: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        width: "100%",
        maxWidth: "1450px",
        margin: "0 auto",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: { padding: "50px 16px" },
        tablet: { padding: "50px 20px" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "48px",
            flexDirection: "column",
            width: "100%",
          },
          children: [
            // Title
            {
              id: generateId(),
              type: "heading" as ElementType,
              content: "Try Us Risk Free",
              styles: {
                color: "forground",
                textAlign: "center",
                fontFamily: "Inter",
                fontSize: "40px",
                fontWeight: "700",
                lineHeight: "120%",
                margin: "0",
              },
              typography: "Heading-2",
              responsiveStyles: {
                mobile: { fontSize: "24px", lineHeight: "32px" },
                tablet: { fontSize: "32px", lineHeight: "100%" },
              },
            },
            // Content
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                alignItems: "stretch",
                justifyContent: "space-between",
                gap: "48px",
                width: "100%",
              },
              responsiveStyles: {
                mobile: { flexDirection: "column" },
              },
              children: [
                // Item 1
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "12px",
                    maxWidth: "400px",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/images/movyo/upsell/Relume.webp",
                      alt: "Free Shipping",
                      styles: {
                        width: "169px",
                        height: "169px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: { width: "100px", height: "100px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "Free Shipping",
                      styles: {
                        color: "forground",
                        fontSize: "24px",
                        fontWeight: "700",
                        lineHeight: "140%",
                        textAlign: "center",
                        margin: "0",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "18px", lineHeight: "24px" },
                        tablet: { fontSize: "18px", lineHeight: "100%" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "Free shipping for all orders over $50.",
                      styles: {
                        textAlign: "center",
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: "400",
                        lineHeight: "150%",
                        margin: "0",
                      },
                      typography: "Default",
                      responsiveStyles: {
                        tablet: { fontSize: "14px", lineHeight: "100%" },
                      },
                    },
                  ],
                },
                // Item 2
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "12px",
                    maxWidth: "400px",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/images/movyo/upsell/Ellipse.webp",
                      alt: "60 days NO questions asked easy returns",
                      styles: {
                        width: "169px",
                        height: "169px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: { width: "100px", height: "100px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "60 days NO questions asked easy returns",
                      styles: {
                        color: "forground",
                        fontSize: "24px",
                        fontWeight: "700",
                        lineHeight: "140%",
                        textAlign: "center",
                        margin: "0",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "18px", lineHeight: "24px" },
                        tablet: { fontSize: "18px", lineHeight: "100%" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        'Try us for 60 nights. Not in love? Full refund according to our <a href="javascript:">Return policy</a>.',
                      styles: {
                        textAlign: "center",
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: "400",
                        lineHeight: "150%",
                        margin: "0",
                      },
                      typography: "Default",
                      responsiveStyles: {
                        tablet: { fontSize: "14px", lineHeight: "100%" },
                      },
                    },
                  ],
                },
                // Item 3
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "12px",
                    maxWidth: "400px",
                    width: "100%",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/images/movyo/upsell/backiocn.webp",
                      alt: "Easy Returns",
                      styles: {
                        width: "169px",
                        height: "169px",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        tablet: { width: "100px", height: "100px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "Easy Returns",
                      styles: {
                        color: "forground",
                        fontSize: "24px",
                        fontWeight: "700",
                        lineHeight: "140%",
                        textAlign: "center",
                        margin: "0",
                      },
                      responsiveStyles: {
                        mobile: { fontSize: "18px", lineHeight: "24px" },
                        tablet: { fontSize: "18px", lineHeight: "100%" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "Simple return policy for your peace of mind.",
                      styles: {
                        textAlign: "center",
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: "400",
                        lineHeight: "150%",
                        margin: "0",
                      },
                      typography: "Default",
                      responsiveStyles: {
                        tablet: { fontSize: "14px", lineHeight: "100%" },
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    bonuses: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        backgroundColor: "background",
        width: "100%",
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "100%",
            maxWidth: "1450px",
            margin: "0 auto",
            padding: "0 50px",
          },
          responsiveStyles: {
            tablet: { maxWidth: "100%", padding: "0 20px" },
            mobile: { maxWidth: "100%", padding: "0 16px" },
          },
          children: [
            // Title and subtitle
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                maxWidth: "1200px",
                margin: "0 auto",
                textAlign: "center",
                padding: "72px 0 0 0",
              },
              responsiveStyles: {
                tablet: { padding: "50px 0 0 0" },
                mobile: { padding: "40px 0 0 0" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content:
                    "Unlock Exclusive Bonuses – Yours FREE With Your Order!",
                  styles: {
                    fontSize: "33px",
                    fontWeight: "700",
                    lineHeight: "49.5px",
                    color: "forground",
                    paddingBottom: "24px",
                    textShadow: "0px 2px 0px rgba(0, 0, 0, 0.04)",
                    margin: "0",
                  },
                  typography: "Heading-4",
                  responsiveStyles: {
                    tablet: {
                      fontSize: "28px",
                      lineHeight: "36px",
                      paddingBottom: "20px",
                    },
                    mobile: {
                      fontSize: "24px",
                      lineHeight: "32px",
                      paddingBottom: "16px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "When you order MOVYO today, you not only get the secret to natural weight loss, you also receive exclusive bonuses designed to supercharge your transformation!",
                  styles: {
                    fontSize: "20px",
                    fontWeight: "400",
                    lineHeight: "24px",
                    color: "forground",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px", lineHeight: "22px" },
                    mobile: { fontSize: "16px", lineHeight: "20px" },
                  },
                },
              ],
            },
            // Bonuses content
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                gap: "81px",
                marginTop: "24px",
              },
              responsiveStyles: {
                tablet: { gap: "20px", marginTop: "20px" },
                mobile: {
                  flexDirection: "column",
                  gap: "30px",
                  marginTop: "16px",
                  alignItems: "center",
                },
              },
              children: [
                // Bonus 1
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "24px",
                    width: "294px",
                  },
                  responsiveStyles: {
                    tablet: { width: "100%", maxWidth: "400px", gap: "20px" },
                    mobile: { width: "100%", gap: "16px" },
                  },
                  children: [
                    // Image + Badge
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        width: "294px",
                        height: "294px",
                        borderRadius: "20px",
                        overflow: "hidden",
                        position: "relative",
                      },
                      responsiveStyles: {
                        tablet: {
                          width: "100%",
                          maxWidth: "300px",
                          height: "auto",
                          aspectRatio: "1/1",
                        },
                        mobile: {
                          width: "100%",
                          maxWidth: "250px",
                          height: "auto",
                          aspectRatio: "1/1",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/images/movyo/bonuse1.webp",
                          alt: "Bonus 1",
                          styles: {
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          },
                        },
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "absolute",
                            bottom: "8px",
                            right: "8.5px",
                            width: "52px",
                            height: "52px",
                          },
                          responsiveStyles: {
                            tablet: { width: "40px", height: "40px" },
                            mobile: { width: "35px", height: "35px" },
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/images/movyo/digitalBadge.webp",
                              alt: "Bonus 1 Badge",
                              styles: {
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Title and description
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        textAlign: "center",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          content:
                            "Love Your Body Again <br>30-Day Reset Guide",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                          },
                          typography: "Button",
                          responsiveStyles: {
                            tablet: { fontSize: "18px", lineHeight: "22px" },
                            mobile: { fontSize: "16px", lineHeight: "20px" },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "This guide provides a simple, step-by-step plan to jumpstart your weight loss journey. Enjoy practical tips and easy routines to transform your body from day one.",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "400",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "18px", lineHeight: "22px" },
                            mobile: { fontSize: "16px", lineHeight: "20px" },
                          },
                        },
                      ],
                    },
                    // Amount
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {},
                      children: [
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            '<span style="text-decoration:line-through;">$47</span> Today: <span style="color:#D4339C;">FREE</span>',
                          styles: {
                            color: "tertiary",
                            fontSize: "28px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            textAlign: "center",
                            margin: "0",
                          },
                          typography: "Heading-5",
                          responsiveStyles: {
                            tablet: { fontSize: "24px", lineHeight: "28px" },
                            mobile: { fontSize: "22px", lineHeight: "26px" },
                          },
                        },
                      ],
                    },
                  ],
                },
                // Bonus 2
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "24px",
                    width: "294px",
                  },
                  responsiveStyles: {
                    tablet: { width: "100%", maxWidth: "400px", gap: "20px" },
                    mobile: { width: "100%", gap: "16px" },
                  },
                  children: [
                    // Image + Badge
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        width: "294px",
                        height: "294px",
                        borderRadius: "20px",
                        overflow: "hidden",
                        position: "relative",
                      },
                      responsiveStyles: {
                        tablet: {
                          width: "100%",
                          maxWidth: "300px",
                          height: "auto",
                          aspectRatio: "1/1",
                        },
                        mobile: {
                          width: "100%",
                          maxWidth: "250px",
                          height: "auto",
                          aspectRatio: "1/1",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/images/movyo/bonuse2.webp",
                          alt: "Bonus 2",
                          styles: {
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          },
                        },
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "absolute",
                            bottom: "8px",
                            right: "5px",
                            width: "52px",
                            height: "52px",
                          },
                          responsiveStyles: {
                            tablet: { width: "40px", height: "40px" },
                            mobile: { width: "35px", height: "35px" },
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/images/movyo/digitalBadge2.webp",
                              alt: "Bonus 2 Badge",
                              styles: {
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Title and description
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        textAlign: "center",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          content: "Forever Fit <br>The Alpine Secret Guide",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                          },
                          typography: "Button",
                          responsiveStyles: {
                            tablet: { fontSize: "18px", lineHeight: "22px" },
                            mobile: { fontSize: "16px", lineHeight: "20px" },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "Discover key strategies to maintain energy, vitality, and a slim figure. Learn timeless French Alpine wisdom for sustainable, lasting results.",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "400",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "18px", lineHeight: "22px" },
                            mobile: { fontSize: "16px", lineHeight: "20px" },
                          },
                        },
                      ],
                    },
                    // Amount
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {},
                      children: [
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            '<span style="text-decoration:line-through;">$100</span> Today: <span style="color:#D4339C;">FREE</span>',
                          styles: {
                            color: "tertiary",
                            fontSize: "28px",
                            fontWeight: "700",
                            lineHeight: "24px",
                            textAlign: "center",
                            margin: "0",
                          },
                          typography: "Heading-5",
                          responsiveStyles: {
                            tablet: { fontSize: "24px", lineHeight: "28px" },
                            mobile: { fontSize: "22px", lineHeight: "26px" },
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    finalThought: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        padding: "107px 0",
        backgroundColor: "background",
        width: "100%",
        maxWidth: "1450px",
        margin: "0 auto",
      },
      responsiveStyles: {
        mobile: { padding: "50px 16px" },
        tablet: { padding: "50px 16px" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            display: "flex",
            alignItems: "stretch",
            justifyContent: "space-between",
            width: "100%",
          },
          responsiveStyles: {
            mobile: { flexDirection: "column-reverse", gap: "30px" },
            tablet: { gap: "25px" },
          },
          children: [
            // Left (Image)
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "45%",
              },
              responsiveStyles: {
                mobile: { width: "100%" },
                tablet: { width: "50%" },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/images/movyo/upsell/final.svg",
                  alt: "Final Thought",
                  styles: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  },
                },
              ],
            },
            // Right (Content)
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "55%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: "48px",
              },
              responsiveStyles: {
                mobile: { width: "100%" },
                tablet: { width: "50%", gap: "25px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "Thank you for considering Movyo!",
                  styles: {
                    color: "forground",
                    fontSize: "58.3px",
                    fontWeight: "700",
                    lineHeight: "70.033px",
                  },
                  responsiveStyles: {
                    mobile: { fontSize: "24px", lineHeight: "32px" },
                    tablet: { fontSize: "32px", lineHeight: "100%" },
                  },
                },
                // Note (list of paragraphs)
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "48px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "I'm proud of you for taking the first step. Even one bottle shows you're ready change.",
                      styles: {
                        fontSize: "20px",
                        lineHeight: "32px",
                        color: "forground",
                        margin: "0",
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "But based on the thousands I've helped, I can tell you: short-term attempts rarely lead to real results. Your body needs time — and MOVYO is designed for consistency.",
                      styles: {
                        fontSize: "20px",
                        lineHeight: "32px",
                        color: "forground",
                        margin: "0",
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "Just 2 more bottles gives your system enough to start rebuilding naturally and keeping the weight off.",
                      styles: {
                        fontSize: "20px",
                        lineHeight: "32px",
                        color: "forground",
                        margin: "0",
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        "If something inside you is saying don't give up yet — trust that voice. It might be your moment. — Alexandra Sterling",
                      styles: {
                        fontSize: "20px",
                        lineHeight: "32px",
                        color: "forground",
                        margin: "0",
                      },
                    },
                  ],
                },
                // Signature
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "0",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "text" as ElementType,
                      content: "Dr. Jane Doe",
                      styles: {
                        color: "link",
                        fontFamily: "Allura",
                        fontSize: "42px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "100%",
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "Formulator of Movyo",
                      styles: {
                        color: "link",
                        fontFamily: "Inter",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "100%",
                        margin: "0",
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    testimonial: {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        paddingBottom: "25.5px",
        backgroundColor: "background",
        width: "100%",
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "1450px",
            maxWidth: "1450px",
            margin: "0 auto",
            padding: "0 50px",
          },
          responsiveStyles: {
            tablet: {
              maxWidth: "100%",
              padding: "0 16px",
              width: "100%",
            },
            mobile: {
              maxWidth: "100%",
              padding: "0 16px",
              width: "100%",
            },
          },
          children: [
            // Testimonials Header
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                textAlign: "center",
                paddingBottom: "64px",
              },
              responsiveStyles: {
                tablet: {
                  paddingBottom: "40px",
                },
                mobile: {
                  paddingBottom: "30px",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "Real People. Real Transformations.",
                  styles: {
                    fontSize: "42px",
                    fontWeight: "700",
                    color: "forground",
                    margin: "0 0 8px 0",
                    lineHeight: "1.2",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "32px",
                    },
                    mobile: {
                      fontSize: "28px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "Inspiring success stories from everyday individuals who reclaimed their lives with MOVYO",
                  styles: {
                    fontSize: "20px",
                    fontWeight: "400",
                    lineHeight: "28px",
                    color: "forground",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                    mobile: {
                      fontSize: "16px",
                      lineHeight: "22px",
                    },
                  },
                },
              ],
            },
            // Testimonials Grid
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "20px",
                flexWrap: "wrap",
              },
              responsiveStyles: {
                tablet: {
                  flexDirection: "row",
                  gap: "15px",
                },
                mobile: {
                  flexDirection: "column",
                  gap: "20px",
                },
              },
              children: [
                // Review 1
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    width: "372px",
                    minWidth: "300px",
                    flex: "1",
                  },
                  responsiveStyles: {
                    tablet: {
                      width: "calc(33.333% - 10px)",
                      minWidth: "200px",
                    },
                    mobile: {
                      width: "100%",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        padding: "17px 29px",
                        border: "1px solid #D9D9D9",
                        backgroundColor: "#FAFAFA",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "8px",
                        height: "100%",
                        boxSizing: "border-box",
                      },
                      responsiveStyles: {
                        tablet: {
                          padding: "15px 20px",
                        },
                        mobile: {
                          padding: "12px 16px",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            justifyContent: "center",
                            gap: "2px",
                            marginBottom: "8px",
                          },
                          children: Array(5)
                            .fill(null)
                            .map(() => ({
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/images/movyo/star.svg",
                              alt: "Star",
                              styles: {
                                width: "36px",
                                height: "36px",
                                objectFit: "contain",
                              },
                            })),
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "I've tried everything for weight loss, but MOVYO is the only thing that actually worked. Lost 25 pounds in 3 months!",
                          styles: {
                            fontSize: "16px",
                            fontWeight: "400",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                            textAlign: "center",
                          },
                          typography: "Default",
                        },
                        {
                          id: generateId(),
                          type: "text" as ElementType,
                          content: "- Sarah M.",
                          styles: {
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#666666",
                            margin: "0",
                          },
                        },
                      ],
                    },
                  ],
                },
                // Review 2
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    width: "372px",
                    minWidth: "300px",
                    flex: "1",
                  },
                  responsiveStyles: {
                    tablet: {
                      width: "calc(33.333% - 10px)",
                      minWidth: "200px",
                    },
                    mobile: {
                      width: "100%",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        padding: "17px 29px",
                        border: "1px solid #D9D9D9",
                        backgroundColor: "#FAFAFA",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "8px",
                        height: "100%",
                        boxSizing: "border-box",
                      },
                      responsiveStyles: {
                        tablet: {
                          padding: "15px 20px",
                        },
                        mobile: {
                          padding: "12px 16px",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            justifyContent: "center",
                            gap: "2px",
                            marginBottom: "8px",
                          },
                          children: Array(5)
                            .fill(null)
                            .map(() => ({
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/images/movyo/star.svg",
                              alt: "Star",
                              styles: {
                                width: "36px",
                                height: "36px",
                                objectFit: "contain",
                              },
                            })),
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "MOVYO gave me back my energy and confidence. I feel like a new person!",
                          styles: {
                            fontSize: "16px",
                            fontWeight: "400",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                            textAlign: "center",
                          },
                          typography: "Default",
                        },
                        {
                          id: generateId(),
                          type: "text" as ElementType,
                          content: "- Michael R.",
                          styles: {
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#666666",
                            margin: "0",
                          },
                        },
                      ],
                    },
                  ],
                },
                // Review 3
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    width: "372px",
                    minWidth: "300px",
                    flex: "1",
                  },
                  responsiveStyles: {
                    tablet: {
                      width: "calc(33.333% - 10px)",
                      minWidth: "200px",
                    },
                    mobile: {
                      width: "100%",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        padding: "17px 29px",
                        border: "1px solid #D9D9D9",
                        backgroundColor: "#FAFAFA",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "8px",
                        height: "100%",
                        boxSizing: "border-box",
                      },
                      responsiveStyles: {
                        tablet: {
                          padding: "15px 20px",
                        },
                        mobile: {
                          padding: "12px 16px",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            justifyContent: "center",
                            gap: "2px",
                            marginBottom: "8px",
                          },
                          children: Array(5)
                            .fill(null)
                            .map(() => ({
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/images/movyo/star.svg",
                              alt: "Star",
                              styles: {
                                width: "36px",
                                height: "36px",
                                objectFit: "contain",
                              },
                            })),
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            "Finally found something that works! MOVYO helped me lose 30 pounds and keep it off.",
                          styles: {
                            fontSize: "16px",
                            fontWeight: "400",
                            lineHeight: "24px",
                            color: "forground",
                            margin: "0",
                            textAlign: "center",
                          },
                          typography: "Default",
                        },
                        {
                          id: generateId(),
                          type: "text" as ElementType,
                          content: "- Jennifer L.",
                          styles: {
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#666666",
                            margin: "0",
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    // Added sections from template2.ts
    mentioned: createMentionedInSection(globalConfig),
    partnerMenu: createPartnerMenuSection(globalConfig),
    listing: createListingSection(globalConfig),
    footerMenu: createFooterMenuSection(globalConfig),
    footerEnd: createFooterEndSection(globalConfig),
  };
};
